import { useId, useState } from 'react'
import type { CommandRuleChange, CommandRules as Rules, CommandRule } from '../api/types'
import { errorMessage } from '../api/client'
import { toast } from '../components/toasts'
import { useUnsavedChanges } from '../components/unsaved'

/** A plain word, as the runner accepts it (`poulpe_protocol::agent_settings::plain_word`). */
const WORD = /^[A-Za-z0-9_./:@%+,-]{1,128}$/
const MAX_WORDS = 32
/** The words of a prefix, or null when the runner would refuse them: at least the program and one more word. */
export function prefixWords(text: string): string[] | null {
  const words = text.trim().split(/\s+/).filter(Boolean)
  return words.length >= 2 && words.length <= MAX_WORDS && words.every(w => WORD.test(w)) && !words[0]?.startsWith('-') ? words : null
}

/**
 * A vendor's saved command rules on the runner (Poulpe-524): Codex's user
 * rules, which Codex owns and reads as each session starts. A person edits
 * one rule's prefix or revokes it; each change names the version of the
 * file it was read from, and the runner refuses it if the file changed.
 * Nothing is restarted: running sessions keep the rules they loaded.
 */
export function CommandRules({ rules, unavailable, change }: { rules: Rules; unavailable: boolean; change: (change: Omit<CommandRuleChange, 'agent'>) => Promise<void> }) {
  const title = useId()
  const [editing, setEditing] = useState<CommandRule | null>(null)
  const [field, setField] = useState('')
  const [confirming, setConfirming] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [uncertain, setUncertain] = useState(false)
  const [changed, setChanged] = useState(false)
  useUnsavedChanges(editing !== null && field.trim() !== editing.pattern.join(' '))
  const locked = busy || unavailable || uncertain
  async function apply(action: CommandRuleChange['action'], line: number, version: string) {
    if (locked) return
    setBusy(true); setProblem(null)
    try {
      await change({ version, line, action })
      setEditing(null); setConfirming(null); setChanged(true)
      toast(action.kind === 'revoke' ? 'Rule revoked.' : 'Rule saved.')
    } catch (error) { setProblem(`${errorMessage(error)} Refresh before trying again.`); setUncertain(true) }
    finally { setBusy(false) }
  }
  // One line, not a group: most agents have no rules Poulpe can read yet.
  if (rules.state === 'unsupported') return <p className="muted">Saved command rules are not supported for this agent yet.</p>
  return <section className="settings-group" aria-labelledby={title}>
    <h3 id={title}>Saved command rules</h3>
    {rules.state === 'unreadable' && <p className="muted">Poulpe could not read this agent’s rules safely, so nothing is shown or changed.</p>}
    {rules.state === 'rules' && <>
      <p className="muted">Codex’s user rules on this machine (<code>rules/default.rules</code>). They apply to every project that uses this Codex configuration, and stay until removed. Codex owns them.</p>
      {!rules.rules.length && <p className="muted">No saved rules.</p>}
      {rules.rules.length > 0 && <ul className="settings-rows" aria-label="Saved command rules">
        {rules.rules.map(rule => {
          const prefix = rule.pattern.join(' ')
          const words = editing?.line === rule.line ? prefixWords(field) : null
          return <li key={rule.line}>
            <div role="group" aria-label={prefix} className="settings-row-line">
              {editing?.line === rule.line
                ? <form className="settings-row-line settings-row-what" onSubmit={e => { e.preventDefault(); if (words) void apply({ kind: 'edit', pattern: words }, rule.line, rules.version) }}>
                    <input aria-label={`Prefix for ${prefix}`} autoComplete="off" spellCheck={false} value={field} disabled={locked} onChange={e => { setField(e.target.value); setProblem(null) }} />
                    <button className="button button-primary" disabled={locked || !words || words.join(' ') === prefix}>{busy ? 'Saving…' : 'Save'}</button>
                    <button type="button" className="button button-quiet" disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
                    {!words && <span className="muted">The program and at least one more word, without quotes or shell syntax.</span>}
                  </form>
                : confirming === rule.line
                  ? <span className="settings-row-line settings-row-what">
                      <span>Revoke <code>{prefix}</code>? New sessions will ask again for these commands.</span>
                      <button type="button" className="button" disabled={locked} onClick={() => void apply({ kind: 'revoke' }, rule.line, rules.version)}>{busy ? 'Revoking…' : 'Revoke'}</button>
                      <button type="button" className="button button-quiet" disabled={busy} onClick={() => setConfirming(null)}>Cancel</button>
                    </span>
                  : <>
                      <span className="settings-row-what"><code>{prefix}</code><span className="muted">Allowed without asking, for commands that start with it</span></span>
                      <button type="button" className="button" disabled={locked} onClick={() => { setConfirming(null); setEditing(rule); setField(prefix); setProblem(null) }}>Edit</button>
                      <button type="button" className="button" disabled={locked} onClick={() => { setEditing(null); setConfirming(rule.line); setProblem(null) }}>Revoke</button>
                    </>}
            </div>
          </li>
        })}
      </ul>}
      {rules.other_lines > 0 && <p className="muted">{rules.other_lines === 1 ? '1 other line is kept as it is; edit it in the file.' : `${rules.other_lines} other lines are kept as they are; edit them in the file.`}</p>}
      {rules.running > 0
        ? <p className="muted">{rules.running === 1 ? '1 running Codex session keeps the rules it started with until it restarts' : `${rules.running} running Codex sessions keep the rules they started with until they restart`}. New sessions see every change.</p>
        : changed && <p className="muted">New Codex sessions see this change.</p>}
    </>}
    {problem && <p role="alert" className="text-danger">{problem}</p>}
  </section>
}
